/**
 * The stats-band matrix's shared grammar (leaderboard-row-stats-band-matrix design § C): columns,
 * rows, dim/amber marking — the same simple-totals matrix `StatsBandBuilder` folds, extracted
 * verbatim off `result/result-stats-band.js` so the leaderboard row expansion can mount it without
 * dragging in the lazy result graph (`result-battle-ground.js`'s import — see that module's own
 * header). This module knows nothing about scene: no tick, no placement, no mount decision. Its
 * primary export is `buildMatrix(current, previous, { layout })`, building the DOM and handing back
 * the changed-cell list its two callers do different things with (`result-stats-band.js` wraps a
 * `reveal()` roll around it; `leaderboard/leaderboard-expansion.js` calls with
 * `previous === current` and never reads `rolls` at all — a rest surface, no roll, no amber by
 * construction). `setExactDetail(cell, value)` is the second export, for the result band's own
 * later display paths — see its own doc comment below.
 *
 * W and L are two columns, not one compound cell (stats-matrix-readability design § 1). `472/566`
 * was the one cell in the matrix that needed a division to mean anything, and its denominator was
 * redundant — the row's own six-type sum. W is the raw wins field; L is `roundsSum − wins`,
 * derived here exactly the way the denominator always was (the original design's "no
 * declarerRounds/defenderRounds fields" precedent: definitional arithmetic over fields already on
 * the wire, not a rule a client copy could drift from — the server only ever increments a wins
 * counter inside a counted bucket, so `wins ≤ roundsSum` by construction). Both cells carry the
 * stats panel's own `sp-standard-up`/`sp-standard-down` tokens — green W, red L — so the matrix
 * and the panel a row below it speak one vocabulary rather than fusing both quantities under the
 * green standard alone; the other twenty value cells stay neutral and the 1-unit group gaps
 * carry the grouping. `WL_HEADER` is the one place the pair's icon token, colour class and tooltip
 * key are spelled.
 *
 * Tick-free is load-bearing, not tidiness: every gsap edge stays result-side, so a roll animated
 * here would drag gsap onto the eager start-screen graph the leaderboard mount rides.
 *
 * `layout` (stats-band-narrow-fit-and-entrance-beat design § Thread 1a, default `"single"` — every
 * existing call site untouched) picks the row assembly: `"single"` is the 15-cell row (lead + 6
 * types + gap + W + L + gap + 4 feats); `"wrapped"` splits into two stacked grids — block A (lead
 * + 6 game-type columns, 14.5 unit-equivalents) and block B (lead + W + L + gap + 4 feat columns,
 * 15.5 unit-equivalents) — each keeping the full row=role grammar (header/declarer/defender) with
 * the role leads repeated in both blocks. Block B's leading spacer (the leaderboard-matrix-narrow-
 * shape brief's Decision B) went with the compound cell that forced it: it existed to absorb a
 * 3-digit-per-side "186/235" string overflowing its 3.5-unit track onto the role-lead icon, and a
 * 3-digit count at the value size now sits inside its own 2.25-unit track with room to spare, so
 * the lead icon separates from W the way it separates from block A's first type cell. The COLUMN
 * tables and cell builders below stay the single source of grammar knowledge either way — only the
 * row assembly forks per layout (review correction: "same code path" for the wrap was factually
 * wrong — shared cell builders, per-layout row assembly is the honest framing).
 *
 * Every value renders through `formatCompact` (design § 2, COPY § Numbers) — three significant
 * digits plus a `k` at or above 1000 — while the roll entries keep the RAW numbers: change detection,
 * dim and amber all stay on raw values, only the display compacts. A cell that already carries a
 * tooltip (the Schneider and Schwarz cells) also carries the exact figure as `data-tooltip-detail`;
 * type, Hand, Ouvert, W and L cells have no tooltip anchor and get none (COPY § Numbers rule 4 —
 * no invented anchors). The detail follows the DISPLAYED value, not the final one, which is why
 * `setExactDetail` is exported beside `buildMatrix`: the result band's rewind shows `from` and must
 * say `from`.
 *
 * Per-cell width reservation is gone with the compound cell (design § 3). In a fixed-track grid the
 * TRACK is the reservation — a tick can't reflow its neighbours, and a cold build and a settled
 * roll agree because neither sets a min-width — while a `ch` reservation sized for the compact form
 * would overshoot the track and overflow every cell symmetrically.
 *
 * Roll entries also carry `fromIsZero`/`toIsZero` (design § Thread 2 step 1), computed here where
 * the raw fields are in scope — `appendValueCell` off `from`/`to === 0`, W and L included now that
 * both are plain count cells. The result band's rewind/bump consume these booleans directly rather
 * than re-deriving marking knowledge from formatted text.
 *
 * Icon fill reuses `stats-panel-enhance.js#ICON_FACTORIES` — no SVG duplication. Factories are
 * called sizeless, so no pixel size is baked into the SVG at all: the box that sizes it is the CSS
 * host (`.sp-icon`, sized off `--sp-icon-unit`, overridden per mount — the result band's placement
 * solve, the leaderboard's container-query unit) and `skat.css`'s `.sp-icon > svg` fills it. (An
 * arbitrary `ICON_DRAW_SIZE = 56` plus a matching inline `width/height: 100%` used to say the same
 * thing twice here; the CSS rule that closed the stats-panel's icon/value misalignment is now its
 * one home.)
 */
import { ICON_FACTORIES } from "./stats-panel-enhance.js";
import { t } from "./i18n.js";
import { formatCompact, exactDetail } from "./compact-numbers.js";

// The six declared-game-type columns, in the spec's ♦♥♠♣ Grand Null order. tooltipKey names an
// EXISTING i18n key (diamonds/hearts/spades/clubs/grand/gameTypeNull) — reused, not duplicated.
const TYPE_COLUMNS = [
  {
    icon: "suit-pip-D",
    tooltipKey: "diamonds",
    declaredField: "declaredDiamonds",
    defendedField: "defendedDiamonds",
  },
  {
    icon: "suit-pip-H",
    tooltipKey: "hearts",
    declaredField: "declaredHearts",
    defendedField: "defendedHearts",
  },
  {
    icon: "suit-pip-S",
    tooltipKey: "spades",
    declaredField: "declaredSpades",
    defendedField: "defendedSpades",
  },
  {
    icon: "suit-pip-C",
    tooltipKey: "clubs",
    declaredField: "declaredClubs",
    defendedField: "defendedClubs",
  },
  {
    icon: "chalice",
    tooltipKey: "grand",
    declaredField: "declaredGrand",
    defendedField: "defendedGrand",
  },
  {
    icon: "null-zero",
    tooltipKey: "gameTypeNull",
    declaredField: "declaredNull",
    defendedField: "defendedNull",
  },
];

// The W and L pair, in that order — the stats panel's own two outcome tokens, carrying the panel's
// own `won`/`lost` labels (StatsPanelBlockGrammar's category-variant builders hardcode the same two
// keys at each call site), so the same two icons speak one vocabulary on both surfaces. The row
// lead already carries the role, so "Won" alone suffices, as "Hand" does. Read by the header icons
// AND by the value cells — `colorClass` is spelled here once, never re-spelled at either site.
const WL_HEADER = [
  { icon: "standard-up", colorClass: "sp-standard-up", tooltipKey: "won" },
  { icon: "standard-down", colorClass: "sp-standard-down", tooltipKey: "lost" },
];

// Hand/Ouvert are the game-PLAYED facts, counted on both rows regardless of outcome — declared
// with Hand, or defended against a Hand declaration; won, lost, overbid alike. They sit on the
// played-as-declarer / played-as-defender line, not the won/lost one, so the header tooltip's
// plain "Hand"/"Ouvert" says exactly what the cells count and neither row needs a cell-level
// tooltip. Schneider/Schwarz are the outcome columns (achieved as declarer, inflicted as
// defender) — their meaning shifts by row, which is what their own cell tooltips guard. The table
// keeps its FEAT_COLUMNS name for the trailing column GROUP it lays out, not as a claim about all
// four.
const FEAT_COLUMNS = [
  {
    icon: "struck-skat",
    tooltipKey: "hand",
    declaredField: "declaredHand",
    defendedField: "defendedHand",
  },
  {
    icon: "fan",
    tooltipKey: "ouvert",
    declaredField: "declaredOuvert",
    defendedField: "defendedOuvert",
  },
  {
    icon: "scissors-open",
    tooltipKey: "schneider",
    declaredField: "featSchneider",
    defendedField: "inflictedSchneider",
    cellTooltipDeclarer: "resultStatsBandCellSchneiderDeclarer",
    cellTooltipDefender: "resultStatsBandCellSchneiderDefender",
  },
  {
    icon: "scissors-closed",
    tooltipKey: "schwarz",
    declaredField: "featSchwarz",
    defendedField: "inflictedSchwarz",
    cellTooltipDeclarer: "resultStatsBandCellSchwarzDeclarer",
    cellTooltipDefender: "resultStatsBandCellSchwarzDefender",
  },
];

function buildIconHost(token) {
  const host = document.createElement("div");
  host.className = "sp-icon";
  host.appendChild(ICON_FACTORIES[token](null));
  return host;
}

function appendSpacer(row) {
  row.appendChild(document.createElement("div"));
}

// Shared by both header shapes below — TYPE_COLUMNS and FEAT_COLUMNS are laid out identically
// (icon + tooltip, one cell each), only the column table differs.
function appendHeaderIcons(row, columns) {
  for (const col of columns) {
    const icon = buildIconHost(col.icon);
    icon.dataset.tooltip = t(col.tooltipKey);
    row.appendChild(icon);
  }
}

// Not folded into `appendHeaderIcons`: the type/feat tables carry no colour class and never will,
// so a colour branch there would be a parameter for a case that doesn't exist.
function appendWlHeaderIcons(row) {
  for (const col of WL_HEADER) {
    const icon = buildIconHost(col.icon);
    icon.dataset.tooltip = t(col.tooltipKey);
    icon.classList.add(col.colorClass);
    row.appendChild(icon);
  }
}

// `buildRow("header")` below reuses the value rows' own shell builder — "rsb-row rsb-${role}"
// with role="header" already yields the exact "rsb-row rsb-header" class every header row wants.
function buildHeaderRow() {
  const row = buildRow("header");
  appendSpacer(row); // the row-lead column: no header icon
  appendHeaderIcons(row, TYPE_COLUMNS);
  appendSpacer(row);
  appendWlHeaderIcons(row);
  appendSpacer(row);
  appendHeaderIcons(row, FEAT_COLUMNS);
  return row;
}

// Block A's header: lead + 6 type icons — no W/L, no feats, so no spacer either.
function buildTypesHeaderRow() {
  const row = buildRow("header");
  appendSpacer(row);
  appendHeaderIcons(row, TYPE_COLUMNS);
  return row;
}

// Block B's header: lead + W + L + spacer + 4 feat icons. No leading spacer — Decision B's own
// restored one existed to absorb the compound W/L cell's text overflowing its 3.5-unit track onto
// the role-lead icon, and the split retired both the cell and that overflow (see the file header).
function buildFeatsHeaderRow() {
  const row = buildRow("header");
  appendSpacer(row);
  appendWlHeaderIcons(row);
  appendSpacer(row);
  appendHeaderIcons(row, FEAT_COLUMNS);
  return row;
}

// A round's own field sum equals its row's rounds-played denominator (result stats band design §
// The emission — "no declarerRounds/defenderRounds fields: each is exactly the sum of its row's
// six type cells"), so the client derives it here rather than reading a duplicated wire field.
function roundsSum(snapshot, role) {
  return TYPE_COLUMNS.reduce(
    (sum, col) =>
      sum +
      snapshot[role === "declarer" ? col.declaredField : col.defendedField],
    0,
  );
}

// Shared by every cell below — dim keys off the raw value being zero, amber off the plain to/from
// diff.
function markCell(cell, isZero, changed) {
  if (isZero) cell.classList.add("rsb-dim");
  if (changed) cell.classList.add("rsb-amber");
}

/**
 * Keeps `cell`'s `data-tooltip-detail` honest for the value it currently DISPLAYS — the exact
 * figure at or above 1000, no attribute below it (where the compact form already is the exact one).
 * A no-op on a cell carrying no `data-tooltip`, since a detail line has nothing to hang under.
 * Exported for `result/result-stats-band.js`'s three other display paths (rewind, commit, bump),
 * which each write a different value into the same cells this module built.
 */
export function setExactDetail(cell, value) {
  if (cell.dataset.tooltip == null) return;
  const exact = exactDetail(value);
  if (exact == null) delete cell.dataset.tooltipDetail;
  else cell.dataset.tooltipDetail = exact;
}

// Returns the cell so a caller that needs to mark it further can — only `appendWlCells` does,
// adding the pair's colour class; the type/feat callers ignore it.
function appendValueCell(row, rolls, to, from, tooltipKey) {
  const changed = to !== from;
  const cell = document.createElement("div");
  cell.className = "sp-value";
  cell.textContent = formatCompact(to);
  markCell(cell, to === 0, changed);
  if (tooltipKey) cell.dataset.tooltip = t(tooltipKey);
  setExactDetail(cell, to); // after the tooltip write above — it reads the anchor, not a flag
  row.appendChild(cell);
  if (changed)
    rolls.push({
      el: cell,
      from,
      to,
      fromIsZero: from === 0,
      toIsZero: to === 0,
    });
  return cell;
}

// W and L: two plain count cells (dim at zero, roll on change, both booleans off the raw number),
// distinguished only by the pair's own colour class. L is derived — see the file header for why
// the losses arithmetic stays client-side beside `roundsSum`.
function appendWlCells(row, rolls, role, current, previous) {
  const winsField = role === "declarer" ? "declarerWins" : "defenderWins";
  const [win, loss] = WL_HEADER;
  appendValueCell(
    row,
    rolls,
    current[winsField],
    previous[winsField],
  ).classList.add(win.colorClass);
  appendValueCell(
    row,
    rolls,
    roundsSum(current, role) - current[winsField],
    roundsSum(previous, role) - previous[winsField],
  ).classList.add(loss.colorClass);
}

function appendLeadCell(row, role) {
  const lead = buildIconHost(
    role === "declarer" ? "gatehouse-claimed" : "battle-standard-pair",
  );
  lead.dataset.tooltip = t(
    role === "declarer" ? "resultDeclarer" : "leaderboardStatGegenspieler",
  );
  row.appendChild(lead);
}

function appendTypeCells(row, rolls, role, current, previous) {
  for (const col of TYPE_COLUMNS) {
    const field = role === "declarer" ? col.declaredField : col.defendedField;
    appendValueCell(row, rolls, current[field], previous[field]);
  }
}

function appendFeatCells(row, rolls, role, current, previous) {
  for (const col of FEAT_COLUMNS) {
    const field = role === "declarer" ? col.declaredField : col.defendedField;
    const tooltipKey =
      role === "declarer" ? col.cellTooltipDeclarer : col.cellTooltipDefender;
    appendValueCell(row, rolls, current[field], previous[field], tooltipKey);
  }
}

function buildRow(role) {
  const row = document.createElement("div");
  row.className = `rsb-row rsb-${role}`;
  return row;
}

// Single layout's value row — the 15-cell shape: lead + 6 types + gap + W + L + gap + 4 feats.
function buildValueRow(role, current, previous) {
  const row = buildRow(role);
  const rolls = [];
  appendLeadCell(row, role);
  appendTypeCells(row, rolls, role, current, previous);
  appendSpacer(row);
  appendWlCells(row, rolls, role, current, previous);
  appendSpacer(row);
  appendFeatCells(row, rolls, role, current, previous);
  return { row, rolls };
}

// Block A's value row: lead + 6 types — no W/L, no feats, so no trailing spacer either.
function buildTypesValueRow(role, current, previous) {
  const row = buildRow(role);
  const rolls = [];
  appendLeadCell(row, role);
  appendTypeCells(row, rolls, role, current, previous);
  return { row, rolls };
}

// Block B's value row: lead + W + L + spacer + 4 feats — see buildFeatsHeaderRow for why the
// leading spacer is gone.
function buildFeatsValueRow(role, current, previous) {
  const row = buildRow(role);
  const rolls = [];
  appendLeadCell(row, role);
  appendWlCells(row, rolls, role, current, previous);
  appendSpacer(row);
  appendFeatCells(row, rolls, role, current, previous);
  return { row, rolls };
}

function buildSingleMatrix(current, previous) {
  const el = document.createElement("div");
  el.className = "stats-band-matrix";
  el.appendChild(buildHeaderRow());
  const declarer = buildValueRow("declarer", current, previous);
  const defender = buildValueRow("defender", current, previous);
  el.appendChild(declarer.row);
  el.appendChild(defender.row);
  return { el, rolls: [...declarer.rolls, ...defender.rolls] };
}

// One block of the wrapped layout — its own `.stats-band-matrix` root (plus a track-list
// modifier class) carrying the full row=role grammar (header + declarer + defender), the same
// three-row shape `buildSingleMatrix` builds, just narrower.
function buildBlock(
  modifierClass,
  headerBuilder,
  rowBuilder,
  current,
  previous,
) {
  const el = document.createElement("div");
  el.className = `stats-band-matrix ${modifierClass}`;
  el.appendChild(headerBuilder());
  const declarer = rowBuilder("declarer", current, previous);
  const defender = rowBuilder("defender", current, previous);
  el.appendChild(declarer.row);
  el.appendChild(defender.row);
  return { el, rolls: [...declarer.rolls, ...defender.rolls] };
}

function buildWrappedMatrix(current, previous) {
  const wrap = document.createElement("div");
  wrap.className = "stats-band-matrix-wrap";
  const types = buildBlock(
    "rsb-block-types",
    buildTypesHeaderRow,
    buildTypesValueRow,
    current,
    previous,
  );
  const feats = buildBlock(
    "rsb-block-feats",
    buildFeatsHeaderRow,
    buildFeatsValueRow,
    current,
    previous,
  );
  wrap.appendChild(types.el);
  wrap.appendChild(feats.el);
  return { el: wrap, rolls: [...types.rolls, ...feats.rolls] };
}

/**
 * Builds the matrix DOM off a `{current, previous}` `StatsBandSnapshot` pair — all cells always
 * present, zeros dimmed, changed-this-round cells landed amber. `layout` (default `"single"`)
 * picks the row assembly — see the file header. Returns `{el, rolls}`: `el` is either the single
 * grid root (class `stats-band-matrix`) or the wrapped root (class `stats-band-matrix-wrap`,
 * containing two `.stats-band-matrix` block children); `rolls` is the changed-cell list
 * (`{el, from, to, fromIsZero, toIsZero}`, `from`/`to` RAW) the cell builders accumulate — the result
 * band's rewind/bump play over it, the leaderboard mount (always `previous === current`) never
 * produces one to begin with.
 */
export function buildMatrix(current, previous, { layout = "single" } = {}) {
  return layout === "wrapped"
    ? buildWrappedMatrix(current, previous)
    : buildSingleMatrix(current, previous);
}
